import React from 'react';
import { RowActions, type RowAction } from '@/components/ui';
import { Task, TaskStage } from '../../../types/Task';
import { usePermissions } from '../../../hooks/usePermissions';

interface TaskActionsProps {
  task: Task;
  onAction: (action: string) => void;
}

interface MenuItem {
  action: string;
  label: string;
  /** RowActions icon class, e.g. `bi-pencil-square`. */
  icon: string;
  showFor: TaskStage[] | 'all';
  requiredPermission: string | null;
}

// Previously a bespoke ContextMenu: a `getBoundingClientRect`-positioned portal with its own
// desktop/mobile viewport flip/clamp math, reimplemented per screen size. RowActions' kebab is
// backed by the shared Dropdown (Headless UI Menu, `anchor="bottom start"`), which already
// portals and repositions itself off the viewport edge - the manual math was dead weight and a
// clipping risk inside a scrolling table. The permission/status gating below is unchanged from
// the old ContextMenu, action-for-action.
const TaskActions: React.FC<TaskActionsProps> = ({ task, onAction }) => {
  const { hasPermission } = usePermissions();
  const taskStatus = task.status as TaskStage;
  const workIdId = task.workIdId ?? undefined;

  const menuItems: MenuItem[] = [
    { action: 'review', label: 'Review Task', icon: 'bi-pencil-square', showFor: 'all', requiredPermission: null },
    { action: 'clone-task', label: 'Clone task', icon: 'bi-copy', showFor: 'all', requiredPermission: null },
    { action: 'print-pdf', label: 'Print PDF', icon: 'bi-file-earmark-pdf', showFor: 'all', requiredPermission: null },
    { action: 'history', label: 'Task History', icon: 'bi-clock-history', showFor: 'all', requiredPermission: null },
    {
      action: 'complete',
      label: 'Report Accomplishment',
      icon: 'bi-check2-circle',
      showFor: [TaskStage.ACTIVE, TaskStage.ACTIVE_MARKED_AS_COMPLETE],
      requiredPermission: 'quantified:accomplishment_submit',
    },
    {
      action: 'convert-to-routine',
      label: 'Convert to Routine',
      icon: 'bi-arrow-repeat',
      showFor: [TaskStage.INTERNAL_REVIEW],
      requiredPermission: 'quantified:review_manage',
    },
    {
      action: 'cancel',
      label: 'Cancel Task',
      icon: 'bi-x-circle',
      showFor: [
        TaskStage.PROPOSED,
        TaskStage.PLANNED,
        TaskStage.ACTIVE,
        TaskStage.ACTIVE_MARKED_AS_COMPLETE,
        TaskStage.INTERNAL_REVIEW,
        TaskStage.ACTIVE_MARKED_AS_COMPLETE,
      ],
      requiredPermission: 'quantified:cancelled_manage',
    },
    ...(workIdId != null
      ? [{ action: 'remove-work-id', label: 'Remove Work ID', icon: 'bi-link-45deg', showFor: 'all' as const, requiredPermission: 'workId:approve' }]
      : []),
    {
      action: 'set-active',
      label: 'Set Active',
      icon: 'bi-play-circle',
      showFor: [TaskStage.ACTIVE_MARKED_AS_COMPLETE, TaskStage.INTERNAL_REVIEW],
      requiredPermission: 'quantified:active_manage',
    },
    // Set Active from Planned
    { action: 'set-active', label: 'Set Active', icon: 'bi-play-circle', showFor: [TaskStage.PLANNED], requiredPermission: 'quantified:planned_manage' },
    // Set Active from Proposed
    { action: 'set-active', label: 'Set Active', icon: 'bi-play-circle', showFor: [TaskStage.PROPOSED], requiredPermission: 'quantified:proposed_manage' },
    {
      action: 'reopen',
      label: 'Reopen',
      icon: 'bi-arrow-counterclockwise',
      showFor: [TaskStage.CLOSED, TaskStage.FINALIZED],
      requiredPermission: 'quantified:closed_manage',
    },
    {
      action: 'set-internal-review',
      label: 'Set to Internal Review',
      icon: 'bi-arrow-return-left',
      showFor: [TaskStage.CLOSED, TaskStage.FINALIZED],
      requiredPermission: 'quantified:closed_manage',
    },
    { action: 'close', label: 'Close Task', icon: 'bi-lock', showFor: [TaskStage.INTERNAL_REVIEW], requiredPermission: 'quantified:review_manage' },
  ];

  const visibleMenuItems = menuItems.filter((item) => {
    if (item.action === 'clone-task') {
      return (
        hasPermission('project:task_create') ||
        hasPermission('project:task_edit') ||
        hasPermission('quantified:task_edit')
      );
    }
    // Always show items with no required permission
    if (!item.requiredPermission) return true;

    // Check if the item should be shown for the current task status
    const isVisibleForStatus = item.showFor === 'all' || item.showFor.includes(taskStatus);

    // Check if user has the required permission
    const hasRequiredPermission = hasPermission(item.requiredPermission);

    // Special cases for specific task statuses
    if (taskStatus === TaskStage.PROPOSED && !hasPermission('quantified:proposed_manage')) {
      return false;
    }
    if (taskStatus === TaskStage.PLANNED && !hasPermission('quantified:planned_manage')) {
      return false;
    }
    if (taskStatus === TaskStage.ACTIVE && !hasPermission('quantified:active_manage')) {
      return false;
    }

    return isVisibleForStatus && hasRequiredPermission;
  });

  const actions: RowAction[] = visibleMenuItems.map((item, i) => ({
    kind: 'custom',
    icon: item.icon,
    label: item.label,
    // set-active appears up to three times (once per originating status) with the same action
    // string; RowActions keys off array index as well as kind, so the duplicate keys are safe.
    onClick: () => {
      console.log('TaskActions - Task:', task);
      onAction(item.action);
    },
    hidden: false,
  }));

  return <RowActions align="right" actions={actions} />;
};

export default TaskActions;
